import React from 'react';
import { X, CheckCircle2, AlertCircle, TrendingDown, Sparkles, Heart, Eraser } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface BaseToastProps {
    title?: string;
    description: string;
    onClose?: () => void;
    icon?: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
}

const BaseToast: React.FC<BaseToastProps> = ({ title, description, onClose, icon, className, children }) => (
    <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 350 }}
        className={cn(
            "relative flex w-full max-w-md gap-4 overflow-hidden rounded-2xl border-2 p-2 shadow-2xl transition-all active:scale-95",
            "bg-white border-gray-100 dark:bg-slate-900 dark:border-slate-800",
            className
        )}
    >
        {icon && (
            <motion.div
                initial={{ scale: 0.5, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring" }}
                className="mt-0.5 flex-shrink-0"
            >
                {icon}
            </motion.div>
        )}
        <div className="flex justify-center flex-1 flex-col gap-0.5 pr-8">
            {title && (
                <h3 className="font-jost text-[18px] font-bold tracking-tight text-inherit">
                    {title}
                </h3>
            )}
            <p className="font-jost text-[14px] font-medium leading-snug text-inherit opacity-90">
                {description}
            </p>
            {children}
        </div>
        {onClose && (
            <button
                onClick={onClose}
                className="absolute right-2 top-2 rounded-full p-1.5 text-inherit opacity-70 hover:opacity-100 hover:bg-black/10 transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        )}
    </motion.div>
);

export const BrandedToast: React.FC<BaseToastProps & { image?: string }> = (props) => (
    <BaseToast
        {...props}
        className="border-[#d8959b]/30 bg-gradient-to-br from-white to-[#fdf8f9]"
        icon={<Sparkles className="h-5 w-5 text-[#d8959b]" />}
    >
        {props.image && (
            <div className="mt-2 overflow-hidden rounded-lg border border-[#d8959b]/20">
                <img src={props.image} alt="Branded content" className="h-32 w-full object-cover" />
            </div>
        )}
    </BaseToast>
);

export const SuccessToast: React.FC<BaseToastProps> = (props) => (
    <BaseToast
        {...props}
        className="border-green-900 bg-green-800 text-white shadow-green-100/20"
        icon={
            <div className="rounded-full bg-green-50 p-2 dark:bg-green-900/40">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
        }
    />
);

export const ErrorToast: React.FC<BaseToastProps> = (props) => (
    <BaseToast
        {...props}
        className="border-red-700 bg-red-500 text-white shadow-red-900/20"
        icon={
            <div className="rounded-full bg-white/20 ">
                <AlertCircle className="h-7 w-7 text-white mt-2" />
            </div>
        }
    />
);

export const PriceDropToast: React.FC<BaseToastProps & { oldPrice?: string; newPrice?: string }> = (props) => (
    <BaseToast
        {...props}
        className="border-[#829672]/30 bg-gradient-to-br from-white to-[#f8faf7]"
        icon={<TrendingDown className="h-5 w-5 text-[#829672]" />}
    >
        {(props.oldPrice || props.newPrice) && (
            <div className="mt-2 flex items-center gap-2 font-montserrat font-bold">
                <span className="text-xs text-gray-400 line-through">{props.oldPrice}</span>
                <span className="text-sm text-[#829672]">{props.newPrice}</span>
            </div>
        )}
    </BaseToast>
);

export const AddFavoriteToast: React.FC<BaseToastProps & { productImage?: string }> = (props) => (
    <BaseToast
        {...props}
        className="border-[#d8959b]/30 bg-[#2d1b1e] !bg-[#daaaaeff] text-stone-900 shadow-xl shadow-[#d8959b]/10"
        icon={
            props.productImage ? (
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-[#d8959b]/20 bg-stone-100">
                    <img
                        src={props.productImage}
                        alt={props.title}
                        className="h-full w-full object-cover transition-opacity duration-300"
                        onLoad={(e) => (e.currentTarget.style.opacity = '1')}
                        style={{ opacity: 0 }}
                    />
                    <div className="absolute bottom-1 right-1 bg-white p-1 rounded-full shadow-md border border-[#d8959b]/20">
                        <Heart className="h-3 w-3 text-[#d8959b]" fill="#d8959b" />
                    </div>
                </div>
            ) : (
                <div className="rounded-full bg-[#d8959b]/10 p-2 border border-[#d8959b]/20">
                    <Heart className="h-5 w-5 text-[#d8959b]" fill="#d8959b" />
                </div>
            )
        }
    />
);

export const RemoveFavoriteToast: React.FC<BaseToastProps & { productImage?: string }> = (props) => (
    <BaseToast
        {...props}
        className="border-zinc-200 bg-[#064e3b] !bg-[#e4e4e7] text-zinc-900 shadow-xl shadow-zinc-200/50"
        icon={
            props.productImage ? (
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-200">
                    <img
                        src={props.productImage}
                        alt={props.title}
                        className="h-full w-full object-cover transition-opacity duration-300"
                        onLoad={(e) => (e.currentTarget.style.opacity = '1')}
                        style={{ opacity: 0 }}
                    />
                    <div className="absolute bottom-1 right-1 bg-zinc-100 p-1 rounded-full shadow-md border border-zinc-200">
                        <Eraser className="h-3 w-3 text-zinc-500" />
                    </div>
                </div>
            ) : (
                <div className="rounded-full bg-zinc-200 p-2 border border-zinc-300">
                    <Eraser className="h-5 w-5 text-zinc-500" />
                </div>
            )
        }
    />
);
